import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSession } from '@supabase/auth-helpers-react';

interface FAQItem {
  q: string;
  a: string;
}

export function ToolFAQ({ items }: { items?: FAQItem[] }) {
  const session = useSession();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!items || items.length === 0) return;

    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-tool-faq-schema', 'true');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: a,
        },
      })),
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [items]);

  if (!items || items.length === 0) return null;
  if (session) return null;

  return (
    <section className="mt-16 pt-12 border-t border-gray-200">
      <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
      <div className="divide-y divide-gray-200 border-t border-b border-gray-200">
        {items.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i}>
              <button
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left group"
                aria-expanded={isOpen}
              >
                <span className="text-base font-semibold text-gray-900 pr-8 group-hover:text-primary-700 transition-colors">
                  {item.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className="pb-5 text-gray-600 leading-relaxed pr-12"
                style={isOpen ? undefined : { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
              >
                <p>{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
